import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { api } from "../../api/client.js";
import { sessionQuery, useSession } from "../../auth/session.js";
import { AuthLayout, ErrorNotice } from "../../components/form.js";

/** Landing page of the verification link; works signed in or out. */
export function VerifyEmailPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const session = useSession();
  const { token } = useSearch({ from: "/verify-email" });

  const verify = useQuery({
    queryKey: ["auth", "verify-email", token],
    queryFn: async () => {
      const result = await api.auth.email.verify(token);
      await queryClient.invalidateQueries({ queryKey: sessionQuery.queryKey });
      return result;
    },
    enabled: token.length > 0,
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
  });

  const next = session.data ? (
    <Link to="/mail" className="text-sm text-accent underline-offset-2 hover:underline">
      {t("auth.verify.continue")}
    </Link>
  ) : (
    <Link to="/login" className="text-sm text-accent underline-offset-2 hover:underline">
      {t("auth.signup.signIn")}
    </Link>
  );

  if (!token || verify.isError) {
    return (
      <AuthLayout title={t("auth.verify.invalidTitle")} subtitle={t("auth.verify.invalidSubtitle")}>
        <div className="flex flex-col gap-4">
          {verify.error ? <ErrorNotice error={verify.error} /> : null}
          {next}
        </div>
      </AuthLayout>
    );
  }

  if (verify.isPending) {
    return (
      <AuthLayout title={t("auth.verify.title")} subtitle={t("common.loading")}>
        <p className="text-sm text-ink-2">{t("auth.verify.checking")}</p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t("auth.verify.doneTitle")} subtitle={t("auth.verify.doneSubtitle")}>
      {next}
    </AuthLayout>
  );
}
